import { useMemo, useCallback } from 'react';
import avo from 'analytics/avo';
import usePDPContext from 'components/consumer/PDP/state/context/usePDPContext';
import usePDPClearanceData from 'data-hooks/useClearanceData/usePDPClearanceData';
import usePDPPrice from 'data-hooks/usePDPPrice';

const usePDPWishlistEvents = () => {
  const pdpContext = usePDPContext();
  const { data: priceData } = usePDPPrice();
  const clearanceData = usePDPClearanceData();
  const productVariantDetails = useMemo(() => {
    const selectedOptionsExtended =
      pdpContext?.productOptions?.data?.selectedOptionsExtended;
    return selectedOptionsExtended
      ? Object.keys(selectedOptionsExtended)
          .reduce((accu, element) => {
            const selectedOptionLabel =
              selectedOptionsExtended[element]?.value?.value;
            return selectedOptionLabel ? [...accu, selectedOptionLabel] : accu;
          }, [])
          .filter(Boolean)
          .join(',')
      : '';
  }, [pdpContext?.productOptions?.data]);

  const productAddedToWishlistEvent = useCallback(() => {
    const product = pdpContext?.basicDetails?.data;
    const productOptionData = pdpContext?.productOptions?.data;
    avo.productAddedToWishlist({
      productId: product?.id,
      fabricId: 0,
      fabricName: '',
      price: priceData?.price?.final_price
        ? parseFloat(priceData?.price?.final_price)
        : 0,
      productFamily: product?.family?.name || '',
      sku: productOptionData?.selectedOptionsVariantSku,
      fabricColor: '',
      fullPrice: priceData?.price?.original_price
        ? parseFloat(priceData?.price?.original_price)
        : 0,
      isClearance: !!clearanceData.isClearance,
      quickship: !!priceData?.productOptions?.data?.stock
        ?.isSelectedOptionsQuickship,

      location: avo.Location.PDP,
      name: product?.name,
      variant: productVariantDetails,
      category: product?.type?.name,
      currency: 'USD',
    });
  }, [pdpContext, priceData, productVariantDetails, clearanceData]);

  const productRemovedFromWishlistEvent = useCallback(() => {
    const product = pdpContext?.basicDetails?.data;
    const productOptionData = pdpContext?.productOptions?.data;
    avo.productRemovedFromWishlist({
      productId: product?.id,
      category: product?.type?.name,
      isClearance: !!clearanceData.isClearance,
      name: product?.name,
      price: priceData?.price?.final_price
        ? parseFloat(priceData?.price?.final_price)
        : 0,
      quantity: 1,
      sku: productOptionData?.selectedOptionsVariantSku,
      variant: productVariantDetails,
      fullPrice: priceData?.price?.original_price
        ? parseFloat(priceData?.price?.original_price)
        : 0,
      productFamily: product?.family?.name || '',
      contentIds: [product?.sku],
      isFlashSale: !!clearanceData.isFlashSale,
      currency: 'USD',
    });
  }, [pdpContext, priceData, productVariantDetails, clearanceData]);

  const productWishlistEvent = useCallback(
    isFavorited => {
      if (!isFavorited) {
        productAddedToWishlistEvent();
      } else {
        productRemovedFromWishlistEvent();
      }
    },
    [productAddedToWishlistEvent, productRemovedFromWishlistEvent]
  );

  return {
    productWishlistEvent,
  };
};

export default usePDPWishlistEvents;
